import { useAuthStore } from '@/stores/authStore'
import { useTrialState } from '@/hooks/useDeploy'

/**
 * Tier-aware entitlements (RR-5 follow-on).
 *
 * Single source of truth for "what can this user see/do, given their plan".
 * The subscription tier comes from `/api/billing/trial-state` (already fetched
 * by useTrialState); platform-admin from the auth store. Pairs with
 * `launchGating.ts` (a feature is usable when its launch flag is on AND the
 * tier permits) — launch flags answer "is this shipped?", entitlements answer
 * "is this user's plan allowed?".
 */
// Both ladders on one scale: individual free→pro→max→ultra, business
// free→pro→team→enterprise. max/ultra slot between pro and team so an
// individual paid user clears `atLeast('pro')` but not the business-only
// `atLeast('team')` gates.
export type Tier =
  | 'anonymous'
  | 'free'
  | 'pro'
  | 'max'
  | 'ultra'
  | 'team'
  | 'enterprise'

/** Deployment surface for the user's tier (Plan 48). */
export type DeploySurface = 'single' | 'sandbox_promote'

// Monotonic rank so callers can do `atLeast('pro')` instead of enumerating tiers.
const TIER_RANK: Record<Tier, number> = {
  anonymous: 0,
  free: 1,
  pro: 2,
  max: 3,
  ultra: 4,
  team: 5,
  enterprise: 6,
}

/** Staging snapshot from trial-state (Plan 48). */
export interface StagingSnapshot {
  deploySurface: DeploySurface
  /** Included persistent-staging envs; null = unlimited. */
  maxStagingEnvironments: number | null
}

const DEFAULT_STAGING: StagingSnapshot = {
  deploySurface: 'single',
  maxStagingEnvironments: 0,
}

export interface Entitlements {
  tier: Tier
  /** pro or above (any paid plan). */
  isPaid: boolean
  /** team/enterprise (business ladder). */
  isBusiness: boolean
  isPlatformAdmin: boolean
  /** True when the user's tier is at least `t`. */
  atLeast: (t: Tier) => boolean
  /** Plan 48 — deployments tier-hybrid surface + staging cap. */
  deploySurface: DeploySurface
  maxStagingEnvironments: number | null
}

/** Pure core — unit-testable without React. */
export function computeEntitlements(
  tier: Tier,
  isPlatformAdmin: boolean,
  staging: StagingSnapshot = DEFAULT_STAGING,
): Entitlements {
  const rank = TIER_RANK[tier] ?? TIER_RANK.free
  return {
    tier,
    isPaid: rank >= TIER_RANK.pro,
    isBusiness: rank >= TIER_RANK.team,
    isPlatformAdmin,
    atLeast: (t: Tier) => rank >= (TIER_RANK[t] ?? 0),
    deploySurface: staging.deploySurface,
    maxStagingEnvironments: staging.maxStagingEnvironments,
  }
}

const _KNOWN_TIERS: ReadonlySet<string> = new Set([
  'free', 'pro', 'max', 'ultra', 'team', 'enterprise',
])

/** Normalize the trial-state `plan` string to a known Tier. */
export function normalizeTier(plan: string | undefined, isAuthed: boolean): Tier {
  const p = (plan || '').toLowerCase()
  if (_KNOWN_TIERS.has(p)) return p as Tier
  // 'anonymous', unknown, or no data → anonymous when logged out, else free.
  return isAuthed ? 'free' : 'anonymous'
}

export function useEntitlements(): Entitlements {
  const user = useAuthStore((s) => s.user)
  const isPlatformAdmin = useAuthStore((s) => s.isPlatformAdmin)
  const isAuthed = !!user
  // Only fetch trial-state for authed users (anon has no plan).
  const { data } = useTrialState(isAuthed)
  const tier = normalizeTier(data?.plan, isAuthed)
  const staging: StagingSnapshot = {
    deploySurface: (data?.deploy_surface as DeploySurface) ?? 'single',
    maxStagingEnvironments:
      data?.max_staging_environments === undefined
        ? 0
        : data.max_staging_environments,
  }
  return computeEntitlements(tier, isPlatformAdmin, staging)
}
